import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FlaskConical,
  Package,
  Warehouse,
  Factory,
  Users,
  FileText,
  ShoppingCart,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Search,
  BarChart3,
  Truck,
  ClipboardList,
  Building2,
  UserCog,
  Boxes,
  GitBranch,
  ClipboardCheck,
  Calculator,
  AlertTriangle,
  DollarSign,
  Moon,
  Sun,
  FileCheck,
  Wrench,
  CalendarDays,
  TrendingUp,
  TrendingDown,
  BellRing,
  Receipt,
  Beaker,
  Tags,
  Tag,
  FileSignature,
  Layers,
  RefreshCw,
  Award,
  Calendar,
  Clock,
  Key,
  FileSearch,
  Gauge,
  Recycle,
  TestTube,
  Scale,
  Landmark,
  PenTool,
  Network,
  History,
  AlertOctagon,
  ShieldCheck,
  Briefcase,
  Send,
  Shield,
  Globe,
  Store,
  LineChart,
  Leaf,
  ArrowRightLeft,
  Microscope,
  Hammer,
  GraduationCap,
  FolderKanban,
  Plug,
  Plus,
  FilePlus,
  UserPlus,
  PackagePlus,
  Activity,
  Mail,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { useHeartbeat } from '../hooks/useHeartbeat';
import SearchModal from './SearchModal';
import KeyboardShortcutsHelp from './KeyboardShortcutsHelp';
import NotificationDropdown from './NotificationDropdown';

// Navigation item types
interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
}

interface NavGroup {
  name: string;
  icon: LucideIcon;
  items: NavItem[];
}

type NavigationItem = NavItem | NavGroup;

const isNavGroup = (item: NavigationItem): item is NavGroup => 'items' in item;

// Grouped navigation structure - all pages
const navigation: NavigationItem[] = [
  // Dashboard & Analytics
  {
    name: 'Dashboard',
    icon: LayoutDashboard,
    items: [
      { name: 'Overview', href: '/app', icon: LayoutDashboard },
      { name: 'Analytics', href: '/app/analytics', icon: BarChart3 },
      { name: 'BI Builder', href: '/app/bi', icon: LineChart },
      { name: 'Reports', href: '/app/reports', icon: FileText },
      { name: 'Report Templates', href: '/app/report-templates', icon: FileText },
      { name: 'Activity Log', href: '/app/activity', icon: History },
      { name: 'AI Insights', href: '/app/ai', icon: LineChart },
    ],
  },

  // Products & R&D
  {
    name: 'Products',
    icon: FlaskConical,
    items: [
      { name: 'Formulas', href: '/app/formulas', icon: FlaskConical },
      { name: 'Raw Materials', href: '/app/raw-materials', icon: Package },
      { name: 'Product Briefs', href: '/app/product-briefs', icon: FileText },
      { name: 'Recipe Development', href: '/app/recipe-development', icon: Beaker },
      { name: 'R&D Lab', href: '/app/lab', icon: Microscope },
      { name: 'Sensory', href: '/app/sensory', icon: Beaker },
      { name: 'Projects', href: '/app/projects', icon: FolderKanban },
    ],
  },

  // Inventory & Warehouse
  {
    name: 'Inventory',
    icon: Warehouse,
    items: [
      { name: 'Stock', href: '/app/inventory', icon: Warehouse },
      { name: 'Warehouses', href: '/app/warehouses', icon: Building2 },
      { name: 'Warehouse Mgmt', href: '/app/warehouse-management', icon: Boxes },
      { name: 'Locations', href: '/app/locations', icon: Building2 },
      { name: 'Advanced', href: '/app/advanced-inventory', icon: TrendingUp },
      { name: 'Transfers', href: '/app/transfers', icon: ArrowRightLeft },
      { name: 'Reorder Points', href: '/app/reorder-points', icon: AlertTriangle },
    ],
  },

  // Production & Planning
  {
    name: 'Production',
    icon: Factory,
    items: [
      { name: 'Orders', href: '/app/production', icon: Factory },
      { name: 'Planning', href: '/app/planning', icon: CalendarDays },
      { name: 'Scheduling', href: '/app/scheduling', icon: Calendar },
      { name: 'MRP', href: '/app/mrp', icon: Network },
      { name: 'Batches', href: '/app/batches', icon: Boxes },
      { name: 'Batch Records', href: '/app/batch-records', icon: ClipboardList },
      { name: 'EBR', href: '/app/ebr', icon: FileCheck },
      { name: 'Equipment', href: '/app/equipment', icon: Wrench },
      { name: 'Maintenance', href: '/app/maintenance', icon: Hammer },
      { name: 'Yield Analysis', href: '/app/yield-analysis', icon: TrendingUp },
      { name: 'Waste', href: '/app/waste', icon: Recycle },
      { name: 'Packaging', href: '/app/packaging', icon: Package },
      { name: 'Contract Mfg', href: '/app/contract-manufacturing', icon: Briefcase },
    ],
  },

  // Sales
  {
    name: 'Sales',
    icon: ShoppingCart,
    items: [
      { name: 'Customers', href: '/app/customers', icon: Users },
      { name: 'CRM', href: '/app/crm', icon: Users },
      { name: 'CRM Management', href: '/app/crm-management', icon: Users },
      { name: 'Customer Feedback', href: '/app/customer-feedback', icon: FileText },
      { name: 'Quotes', href: '/app/quotes', icon: FileText },
      { name: 'Orders', href: '/app/orders', icon: ShoppingCart },
      { name: 'Order Management', href: '/app/order-management', icon: ClipboardList },
      { name: 'Shipping', href: '/app/shipping', icon: Send },
      { name: 'Samples', href: '/app/samples', icon: Beaker },
      { name: 'Shopify', href: '/app/shopify', icon: Store },
    ],
  },

  // Procurement
  {
    name: 'Procurement',
    icon: Truck,
    items: [
      { name: 'Suppliers', href: '/app/suppliers', icon: Truck },
      { name: 'Supplier Performance', href: '/app/supplier-performance', icon: TrendingUp },
      { name: 'Vendor Qualification', href: '/app/vendor-qualification', icon: ShieldCheck },
      { name: 'Purchasing', href: '/app/purchasing', icon: ClipboardList },
      { name: 'RFQs', href: '/app/rfqs', icon: FileSearch },
      { name: 'Procurement', href: '/app/procurement', icon: ShoppingCart },
      { name: 'Contracts', href: '/app/contracts', icon: FileSignature },
      { name: 'Supply Chain Risks', href: '/app/supply-chain-risks', icon: AlertOctagon },
    ],
  },

  // Quality
  {
    name: 'Quality',
    icon: ClipboardCheck,
    items: [
      { name: 'Quality Control', href: '/app/quality', icon: ClipboardCheck },
      { name: 'CAPA', href: '/app/capa', icon: AlertTriangle },
      { name: 'SPC', href: '/app/spc', icon: Gauge },
      { name: 'Specifications', href: '/app/specifications', icon: FileCheck },
      { name: 'Certifications', href: '/app/certifications', icon: Award },
      { name: 'Validation', href: '/app/validation', icon: ShieldCheck },
      { name: 'Calibration', href: '/app/calibration', icon: Scale },
      { name: 'Allergens', href: '/app/allergens', icon: AlertTriangle },
      { name: 'Stability', href: '/app/stability', icon: TestTube },
      { name: 'Traceability', href: '/app/traceability', icon: GitBranch },
      { name: 'Genealogy', href: '/app/genealogy', icon: GitBranch },
      { name: 'SDS', href: '/app/sds', icon: FileText },
      { name: 'EHS', href: '/app/ehs', icon: Shield },
      { name: 'Shelf Life', href: '/app/shelf-life', icon: Clock },
      { name: 'Deviations', href: '/app/deviations', icon: AlertTriangle },
      { name: 'NCR', href: '/app/ncr', icon: AlertOctagon },
      { name: 'OOS', href: '/app/oos', icon: AlertTriangle },
      { name: 'Change Control', href: '/app/change-control', icon: RefreshCw },
      { name: 'Complaints & CAPA', href: '/app/complaints-capa', icon: FileText },
      { name: 'GC/MS', href: '/app/gcms', icon: Microscope },
    ],
  },

  // Finance
  {
    name: 'Finance',
    icon: DollarSign,
    items: [
      { name: 'Costing', href: '/app/costing', icon: Calculator },
      { name: 'Cost Optimization', href: '/app/cost-optimization', icon: TrendingDown },
      { name: 'Batch Costing', href: '/app/batch-costing', icon: Layers },
      { name: 'Exchange Rates', href: '/app/costing/exchange-rates', icon: RefreshCw },
      { name: 'Supplier Materials', href: '/app/costing/supplier-materials', icon: Package },
      // { name: 'Financial Analytics', href: '/app/financial-analytics', icon: BarChart3 }, // Hidden per stakeholder request - confidential
      { name: 'Pricing Engine', href: '/app/pricing-engine', icon: Tag },
      { name: 'Price Lists', href: '/app/price-lists', icon: Tags },
      { name: 'Invoices', href: '/app/invoices', icon: Receipt },
      { name: 'Payments', href: '/app/payments', icon: DollarSign },
    ],
  },

  // Compliance
  {
    name: 'Compliance',
    icon: Shield,
    items: [
      { name: 'Audit Trail', href: '/app/audit', icon: ClipboardCheck },
      { name: 'Audit Management', href: '/app/audit-management', icon: FileCheck },
      { name: 'Compliance', href: '/app/compliance', icon: Shield },
      { name: 'Regulatory', href: '/app/regulatory', icon: Globe },
      { name: 'E-Signatures', href: '/app/esignatures', icon: PenTool },
      { name: 'Documents', href: '/app/documents', icon: FileText },
      { name: 'IP Management', href: '/app/ip-management', icon: Landmark },
      { name: 'Sustainability', href: '/app/sustainability', icon: Leaf },
      { name: 'Training', href: '/app/training', icon: GraduationCap },
    ],
  },

  // Settings
  {
    name: 'Settings',
    icon: Settings,
    items: [
      { name: 'Settings', href: '/app/settings', icon: Settings },
      { name: 'Users', href: '/app/users', icon: UserCog },
      { name: 'User Activity', href: '/app/user-activity', icon: Activity },
      { name: 'Email Inbox', href: '/app/emails', icon: Mail },
      { name: 'Notifications', href: '/app/alerts', icon: BellRing },
      { name: 'Notification Settings', href: '/app/notification-settings', icon: BellRing },
      { name: 'API Keys', href: '/app/api-keys', icon: Key },
      { name: 'Webhooks', href: '/app/webhooks', icon: Network },
      { name: 'Workflows', href: '/app/workflows', icon: GitBranch },
      { name: 'Integrations', href: '/app/integrations', icon: Plug },
    ],
  },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('nav-expanded-groups');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const quickActionsRef = useRef<HTMLDivElement>(null);
  const navScrollRef = useRef<HTMLElement | null>(null);
  const mobileNavScrollRef = useRef<HTMLElement | null>(null);
  const savedScrollRef = useRef({ desktop: 0, mobile: 0 });
  const { user, logout } = useAuth();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const keySequence = useRef<string[]>([]);
  const keyTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track user activity for presence/analytics
  useHeartbeat();

  // Toggle group expansion - preserve scroll position to prevent jumps
  const toggleGroup = useCallback((groupName: string) => {
    const nav = navScrollRef.current;
    const mobileNav = mobileNavScrollRef.current;
    const desktopScrollTop = nav?.scrollTop ?? 0;
    const mobileScrollTop = mobileNav?.scrollTop ?? 0;

    // Restore function
    const restoreScroll = () => {
      if (nav) nav.scrollTop = desktopScrollTop;
      if (mobileNav) mobileNav.scrollTop = mobileScrollTop;
    };

    setExpandedGroups(prev => {
      const next = { ...prev, [groupName]: !prev[groupName] };
      try {
        localStorage.setItem('nav-expanded-groups', JSON.stringify(next));
      } catch {}
      return next;
    });

    // Restore immediately, after microtask, after RAF, and after transition
    restoreScroll();
    queueMicrotask(restoreScroll);
    requestAnimationFrame(restoreScroll);
    setTimeout(restoreScroll, 0);
    setTimeout(restoreScroll, 50);
    setTimeout(restoreScroll, 200); // After CSS transition completes
  }, []);

  // Check if a group contains the active route
  const isGroupActive = (group: NavGroup) => {
    return group.items.some(item => location.pathname === item.href || location.pathname.startsWith(item.href + '/'));
  };

  // Auto-expand group containing active route (only on path change)
  // Preserve scroll position to prevent jumps
  useEffect(() => {
    const desktopScrollTop = navScrollRef.current?.scrollTop ?? 0;
    const mobileScrollTop = mobileNavScrollRef.current?.scrollTop ?? 0;

    setExpandedGroups(prev => {
      let changed = false;
      const updates: Record<string, boolean> = {};
      navigation.forEach(item => {
        if (isNavGroup(item) && isGroupActive(item) && !prev[item.name]) {
          updates[item.name] = true;
          changed = true;
        }
      });
      if (!changed) return prev;
      const next = { ...prev, ...updates };
      try {
        localStorage.setItem('nav-expanded-groups', JSON.stringify(next));
      } catch {}
      return next;
    });

    // Restore scroll position
    const restoreScroll = () => {
      if (navScrollRef.current) navScrollRef.current.scrollTop = desktopScrollTop;
      if (mobileNavScrollRef.current) mobileNavScrollRef.current.scrollTop = mobileScrollTop;
    };
    requestAnimationFrame(restoreScroll);
    setTimeout(restoreScroll, 50);
    setTimeout(restoreScroll, 200);
  }, [location.pathname]);

  // Set nav ref and attach scroll listener to track position
  const setNavRef = useCallback((node: HTMLElement | null) => {
    // Remove old listener if exists
    if (navScrollRef.current) {
      navScrollRef.current.removeEventListener('scroll', handleNavScroll);
    }
    navScrollRef.current = node;
    // Attach new listener
    if (node) {
      node.addEventListener('scroll', handleNavScroll, { passive: true });
      // Restore saved position immediately
      node.scrollTop = savedScrollRef.current.desktop;
    }
  }, []);

  // Track scroll position continuously
  const handleNavScroll = useCallback(() => {
    if (navScrollRef.current) {
      savedScrollRef.current.desktop = navScrollRef.current.scrollTop;
    }
  }, []);

  // Restore scroll position after every render using useLayoutEffect
  useLayoutEffect(() => {
    if (navScrollRef.current && savedScrollRef.current.desktop > 0) {
      navScrollRef.current.scrollTop = savedScrollRef.current.desktop;
    }
    if (mobileNavScrollRef.current && savedScrollRef.current.mobile > 0) {
      mobileNavScrollRef.current.scrollTop = savedScrollRef.current.mobile;
    }
  });

  // Close mobile sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
    setQuickActionsOpen(false);
  }, [location.pathname]);

  // Close quick actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (quickActionsRef.current && !quickActionsRef.current.contains(e.target as Node)) {
        setQuickActionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cmd+K keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Keyboard navigation shortcuts (G+key for "Go to", N+key for "New")
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if typing in an input
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Skip modifier keys
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toLowerCase();

      // Clear timeout if exists
      if (keyTimeout.current) {
        clearTimeout(keyTimeout.current);
      }

      // Add key to sequence
      keySequence.current.push(key);

      // Set timeout to clear sequence
      keyTimeout.current = setTimeout(() => {
        keySequence.current = [];
      }, 500);

      // Check for two-key sequences
      if (keySequence.current.length >= 2) {
        const [first, second] = keySequence.current.slice(-2);

        // "G" + key = Go to page
        if (first === 'g') {
          e.preventDefault();
          const goRoutes: Record<string, string> = {
            'h': '/app',           // Home/Dashboard
            'f': '/app/formulas',  // Formulas
            'r': '/app/raw-materials', // Raw materials
            'i': '/app/inventory', // Inventory
            'p': '/app/production', // Production
            'c': '/app/customers', // Customers
            'o': '/app/orders',    // Orders
            'q': '/app/quotes',    // Quotes
            's': '/app/settings',  // Settings
            't': '/app/traceability', // Traceability
          };
          if (goRoutes[second]) {
            navigate(goRoutes[second]);
            keySequence.current = [];
          }
        }

        // "N" + key = New item
        if (first === 'n') {
          e.preventDefault();
          const newRoutes: Record<string, string> = {
            'f': '/app/formulas/new',      // New formula
            'r': '/app/raw-materials/new', // New raw material
            'c': '/app/customers/new',     // New customer
            'o': '/app/orders/new',        // New order
            'q': '/app/quotes/new',        // New quote
            'p': '/app/production/new',    // New production order
          };
          if (newRoutes[second]) {
            navigate(newRoutes[second]);
            keySequence.current = [];
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (keyTimeout.current) clearTimeout(keyTimeout.current);
    };
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const isDark = resolvedTheme === 'dark';

  const SidebarContent = ({ isMobile = false }: { isMobile?: boolean }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center h-16 px-4 border-b ${isDark ? 'border-white/10' : 'border-gray-200/80'} ${isCollapsed && !isMobile ? 'justify-center' : 'gap-3'}`}>
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 shadow-lg shadow-orange-500/30">
          <FlaskConical className="h-5 w-5 text-white" />
        </div>
        {(!isCollapsed || isMobile) && (
          <div className="flex items-center">
            <img src="/logos/froma-original.png" alt="Froma" className={`h-9 w-auto ${isDark ? 'brightness-0 invert' : ''}`} />
          </div>
        )}
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(false)}
            className={`ml-auto p-2 rounded-lg transition-colors ${isDark ? 'text-white/60 hover:text-white hover:bg-white/10' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav
        aria-label="Main navigation"
        ref={isMobile ? (node) => {
          // Track mobile nav scroll - clean up previous listener
          const prev = mobileNavScrollRef.current;
          if (prev && (prev as any).__scrollHandler) {
            prev.removeEventListener('scroll', (prev as any).__scrollHandler);
          }
          mobileNavScrollRef.current = node;
          if (node) {
            const handler = () => {
              savedScrollRef.current.mobile = node.scrollTop;
            };
            (node as any).__scrollHandler = handler;
            node.addEventListener('scroll', handler, { passive: true });
            node.scrollTop = savedScrollRef.current.mobile;
          }
        } : setNavRef}
        className="flex-1 px-3 py-4 overflow-y-auto"
        style={{ overflowAnchor: 'none', contain: 'layout' }}
      >
        <ul className="space-y-1">
          {navigation.map((item) => (
            <li key={item.name}>
              {isNavGroup(item) ? (
                // Collapsible group
                <div>
                  <button
                    type="button"
                    tabIndex={-1}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => toggleGroup(item.name)}
                    className={`w-full group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-150 ${
                      isGroupActive(item)
                        ? isDark
                          ? 'text-orange-400'
                          : 'text-orange-600'
                        : isDark
                          ? 'text-white/70 hover:bg-white/10 hover:text-white'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    } ${isCollapsed && !isMobile ? 'justify-center' : ''}`}
                  >
                    <item.icon className={`h-5 w-5 ${isGroupActive(item) ? (isDark ? 'text-orange-400' : 'text-orange-600') : isDark ? 'text-white/60' : 'text-slate-500'}`} />
                    {(!isCollapsed || isMobile) && (
                      <>
                        <span className="flex-1 text-left">{item.name}</span>
                        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expandedGroups[item.name] ? 'rotate-180' : ''}`} />
                      </>
                    )}
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-200 ease-in-out ${
                      expandedGroups[item.name] && (!isCollapsed || isMobile)
                        ? 'max-h-[40rem] opacity-100'
                        : 'max-h-0 opacity-0'
                    }`}
                  >
                    <ul className="ml-4 mt-1 space-y-0.5 border-l-2 border-slate-200/50 dark:border-white/10 pl-3">
                      {item.items.map((subItem) => (
                        <li key={subItem.href}>
                          <NavLink
                            to={subItem.href}
                            end={subItem.href === '/app'}
                            className={({ isActive }) =>
                              `group relative flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-all duration-150 outline-none ${
                                isActive
                                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-md shadow-orange-500/20'
                                  : isDark
                                    ? 'text-white/60 hover:bg-white/5 hover:text-white'
                                    : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
                              }`
                            }
                          >
                            <subItem.icon className="h-4 w-4" />
                            <span>{subItem.name}</span>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                // Single item (Dashboard)
                <NavLink
                  to={item.href}
                  end={item.href === '/app'}
                  className={({ isActive }) =>
                    `group relative flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all duration-150 outline-none ${
                      isActive
                        ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/25'
                        : isDark
                          ? 'text-white/70 hover:bg-white/10 hover:text-white'
                          : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    } ${isCollapsed && !isMobile ? 'justify-center' : ''}`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <item.icon className={`h-5 w-5 ${isActive ? 'text-white' : isDark ? 'text-white/70 group-hover:text-white' : 'text-slate-500 group-hover:text-slate-700'}`} />
                      {(!isCollapsed || isMobile) && (
                        <>
                          <span className="flex-1">{item.name}</span>
                          {isActive && (
                            <div className="absolute right-2">
                              <ChevronRight className="h-4 w-4" />
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </nav>

      {/* User Section */}
      <div className={`border-t p-3 ${isDark ? 'border-white/10' : 'border-gray-200/80'}`}>
        <div className={`mb-3 flex items-center gap-3 rounded-xl backdrop-blur px-3 py-3 ${isDark ? 'bg-white/5' : 'bg-slate-50'} ${isCollapsed && !isMobile ? 'justify-center' : ''}`}>
          <div className="relative">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-sm font-bold text-white shadow-lg shadow-orange-500/20">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-500 border-2 ${isDark ? 'border-gray-900' : 'border-white'}`} />
          </div>
          {(!isCollapsed || isMobile) && (
            <div className="flex-1 min-w-0">
              <p className={`truncate text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{user?.name}</p>
              <p className={`truncate text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>{user?.email}</p>
            </div>
          )}
        </div>
        <div className={`flex gap-2 ${isCollapsed && !isMobile ? 'flex-col' : ''}`}>
          <NavLink
            to="/app/settings"
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm transition-all outline-none ${isDark ? 'text-white/70 hover:bg-white/10 hover:text-white' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'} ${isCollapsed && !isMobile ? 'px-2' : ''}`}
          >
            <Settings className="h-4 w-4" />
            {(!isCollapsed || isMobile) && <span>Settings</span>}
          </NavLink>
          <button
            onClick={handleLogout}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm transition-all ${isDark ? 'text-white/70 hover:bg-red-500/20 hover:text-red-400' : 'text-slate-600 hover:bg-red-50 hover:text-red-600'} ${isCollapsed && !isMobile ? 'px-2' : ''}`}
          >
            <LogOut className="h-4 w-4" />
            {(!isCollapsed || isMobile) && <span>Logout</span>}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900' : 'bg-slate-50'}`}>
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <>
          <div
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          />
          <aside
            aria-label="Sidebar navigation"
            className={`fixed inset-y-0 left-0 z-50 w-72 lg:hidden ${isDark ? 'bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950' : 'bg-white border-r border-gray-200'}`}
          >
            <SidebarContent isMobile />
          </aside>
        </>
      )}

      {/* Desktop sidebar */}
      <aside
        aria-label="Sidebar navigation"
        className={`hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:flex lg:flex-col transition-all duration-200 ${isCollapsed ? 'lg:w-20' : 'lg:w-64'} ${isDark ? 'bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950' : 'bg-white border-r border-gray-200/80'}`}
      >
        <SidebarContent />
        {/* Collapse toggle */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`absolute -right-3 top-20 z-50 flex h-6 w-6 items-center justify-center rounded-full shadow-lg ${isDark ? 'bg-gray-800 text-white/70 hover:text-white border border-white/10' : 'bg-white text-slate-500 hover:text-slate-700 border border-gray-200'}`}
        >
          <ChevronRight className={`h-3 w-3 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`} />
        </button>
      </aside>

      {/* Main content area */}
      <div className={`flex flex-col min-h-screen transition-all duration-300 ${isCollapsed ? 'lg:pl-20' : 'lg:pl-64'}`}>

        {/* Top header */}
        <header className={`sticky top-0 z-30 backdrop-blur-xl border-b transition-colors duration-200 ${isDark ? 'bg-gray-900/80 border-gray-700/50' : 'bg-white/90 border-gray-200'}`}>
          <div className="flex h-16 items-center gap-4 px-4 lg:px-6">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={sidebarOpen}
              className={`lg:hidden flex items-center justify-center w-10 h-10 rounded-xl transition-colors ${isDark ? 'bg-sky-900/30 text-gray-300 hover:bg-sky-800/40' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
            >
              <Menu className="h-5 w-5" />
            </button>

            {/* Search bar */}
            <div className="flex-1 max-w-xl">
              <button
                onClick={() => setSearchOpen(true)}
                className="w-full relative group"
              >
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 transition-colors ${isDark ? 'text-gray-500 group-hover:text-orange-500' : 'text-slate-400 group-hover:text-orange-500'}`} />
                <div className={`w-full h-10 pl-10 pr-4 rounded-xl text-sm text-left flex items-center transition-all cursor-pointer truncate ${isDark ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 border-0' : 'bg-white text-slate-500 hover:bg-slate-50 border border-gray-200'}`}>
                  <span className="sm:hidden">Search...</span>
                  <span className="hidden sm:inline">Search formulas, materials, orders...</span>
                </div>
                <kbd className={`absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex h-5 items-center gap-1 rounded border px-1.5 font-mono text-[10px] ${isDark ? 'border-gray-600 bg-gray-700 text-gray-400' : 'border-gray-200 bg-slate-50 text-slate-500'}`}>
                  <span>⌘</span>K
                </kbd>
              </button>
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* Quick Actions */}
              <div className="relative" ref={quickActionsRef}>
                <button
                  onClick={() => setQuickActionsOpen(!quickActionsOpen)}
                  className="relative flex items-center gap-2 h-10 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium shadow-md shadow-amber-500/25 dark:shadow-orange-500/30 hover:from-amber-600 hover:to-orange-600 transition-all"
                >
                  <Plus className="h-4 w-4" />
                  <span className="hidden sm:inline">Quick Add</span>
                </button>
                {quickActionsOpen && (
                  <div className={`absolute right-0 mt-2 w-56 rounded-xl shadow-xl border py-2 z-50 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                      <button
                        onClick={() => navigate('/app/formulas/new')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        <FilePlus className="h-4 w-4 text-orange-500" />
                        New Formula
                        <kbd className={`ml-auto text-xs font-mono ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>N F</kbd>
                      </button>
                      <button
                        onClick={() => navigate('/app/raw-materials/new')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        <PackagePlus className="h-4 w-4 text-blue-500" />
                        New Raw Material
                        <kbd className={`ml-auto text-xs font-mono ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>N R</kbd>
                      </button>
                      <button
                        onClick={() => navigate('/app/customers/new')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        <UserPlus className="h-4 w-4 text-green-500" />
                        New Customer
                        <kbd className={`ml-auto text-xs font-mono ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>N C</kbd>
                      </button>
                      <button
                        onClick={() => navigate('/app/orders/new')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        <ShoppingCart className="h-4 w-4 text-purple-500" />
                        New Order
                        <kbd className={`ml-auto text-xs font-mono ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>N O</kbd>
                      </button>
                      <button
                        onClick={() => navigate('/app/quotes/new')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        <FileText className="h-4 w-4 text-amber-500" />
                        New Quote
                        <kbd className={`ml-auto text-xs font-mono ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>N Q</kbd>
                      </button>
                      <div className={`border-t my-2 ${isDark ? 'border-gray-700' : 'border-gray-100'}`} />
                      <button
                        onClick={() => navigate('/app/production/new')}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-slate-700 hover:bg-slate-50'}`}
                      >
                        <Factory className="h-4 w-4 text-indigo-500 flex-shrink-0" />
                        <span className="whitespace-nowrap">Production Order</span>
                        <kbd className={`ml-auto text-xs font-mono flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>N P</kbd>
                      </button>
                  </div>
                )}
              </div>

              {/* Theme Toggle */}
              <div className="relative">
                <button
                  onClick={() => {
                    setTheme(isDark ? 'light' : 'dark');
                  }}
                  className={`flex items-center justify-center w-10 h-10 rounded-xl border transition-colors ${isDark ? 'bg-sky-900/30 border-sky-700/40 text-gray-300 hover:bg-sky-800/40 hover:text-white' : 'bg-white/80 border-gray-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 shadow-sm'}`}
                  title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
                >
                  {isDark ? (
                    <Sun className="h-5 w-5" />
                  ) : (
                    <Moon className="h-5 w-5" />
                  )}
                </button>
              </div>

              {/* Notifications */}
              <NotificationDropdown />

              {/* User avatar (mobile) */}
              <button
                className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 text-white text-sm font-bold shadow-lg shadow-orange-500/20"
              >
                {user?.name?.charAt(0).toUpperCase()}
              </button>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main id="main-content" role="main" className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>

      {/* Global Search Modal */}
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcutsHelp />
    </div>
  );
}
